import React, { useEffect, useState } from 'react';

const ChatBubble = ({ role, content }) => {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap shadow-sm ${
          isUser ? 'bg-[#D9FDD3] text-gray-900 rounded-br-sm' : 'bg-white text-gray-900 rounded-bl-sm border border-gray-100'
        }`}
      >
        {content || (isUser ? '(empty)' : '[No reply]')}
      </div>
    </div>
  );
};

/**
 * One user/assistant exchange row in a run report, with optional edit and delete.
 */
const ReportExchangeBlock = ({
  exchange,
  disabled = false,
  saving = false,
  onUpdate,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [answerDraft, setAnswerDraft] = useState('');
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!isEditing) {
      setMessageDraft(exchange?.message || '');
      setAnswerDraft(exchange?.answer || '');
    }
  }, [exchange?.message, exchange?.answer, isEditing]);

  const canEdit = typeof onUpdate === 'function';
  const canDelete = typeof onDelete === 'function';
  const showActions = (canEdit || canDelete) && !disabled;

  const handleStartEdit = () => {
    setLocalError(null);
    setMessageDraft(exchange?.message || '');
    setAnswerDraft(exchange?.answer || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setLocalError(null);
    setIsEditing(false);
    setMessageDraft(exchange?.message || '');
    setAnswerDraft(exchange?.answer || '');
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setLocalError(null);
    try {
      const result = await onUpdate({ message: messageDraft, answer: answerDraft });
      if (result?.success === false) {
        setLocalError(result?.error || 'Failed to save exchange.');
        return;
      }
      setIsEditing(false);
    } catch (err) {
      setLocalError(err?.message || 'Failed to save exchange.');
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    if (!window.confirm('Delete this exchange? This cannot be undone.')) return;
    setLocalError(null);
    try {
      const result = await onDelete();
      if (result?.success === false) {
        setLocalError(result?.error || 'Failed to delete exchange.');
      }
    } catch (err) {
      setLocalError(err?.message || 'Failed to delete exchange.');
    }
  };

  if (isEditing) {
    return (
      <div className="rounded-lg border border-[#00A884]/30 bg-white p-2 space-y-2">
        <div>
          <label className="block text-[10px] font-medium text-gray-500 mb-0.5">User message</label>
          <textarea
            value={messageDraft}
            onChange={(e) => setMessageDraft(e.target.value)}
            rows={2}
            disabled={saving}
            className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-gray-800 focus:ring-2 focus:ring-[#00A884]/30 focus:outline-none disabled:opacity-50"
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Assistant reply</label>
          <textarea
            value={answerDraft}
            onChange={(e) => setAnswerDraft(e.target.value)}
            rows={3}
            disabled={saving}
            className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-gray-800 focus:ring-2 focus:ring-[#00A884]/30 focus:outline-none disabled:opacity-50"
          />
        </div>
        {localError && <div className="text-[11px] text-red-600">{localError}</div>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-2 py-1 text-xs rounded bg-[#00A884] text-white hover:bg-[#06CF9C] disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="px-2 py-1 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative space-y-2">
      {showActions && (
        <div className="absolute -top-1 right-0 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition">
          {canEdit && (
            <button
              type="button"
              onClick={handleStartEdit}
              disabled={saving}
              className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50"
              title="Edit this exchange"
            >
              Edit
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="px-1.5 py-0.5 text-[10px] rounded border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-50"
              title="Delete this exchange"
            >
              Delete
            </button>
          )}
        </div>
      )}
      <ChatBubble role="user" content={exchange?.message} />
      <ChatBubble role="assistant" content={exchange?.answer} />
      {localError && <div className="text-[11px] text-red-600 text-right">{localError}</div>}
    </div>
  );
};

export default ReportExchangeBlock;
